import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ProjectPlan } from "@/types/api";
import { PlanPicker } from "./plan-picker";

interface PlanFilterScreenProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: ProjectPlan[];
  selectedIds: string[];
  onToggle: (planId: string) => void;
}

/**
 * Full-screen plan picker for mobile. Search is pinned at the top, the list
 * is the only scroll region, and a sticky "Done" button sits at the bottom
 * as a dismiss affordance — selections still apply instantly as rows are
 * tapped. Rendered at `z-[80]` so it sits above the filter drawer (`z-[70]`).
 */
export function PlanFilterScreen({
  open,
  onOpenChange,
  plans,
  selectedIds,
  onToggle,
}: PlanFilterScreenProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        bare
        showCloseButton={false}
        overlayClassName="z-[80]"
        className="z-[80] h-dvh max-h-none w-screen max-w-none rounded-none border-none left-0 top-0 translate-x-0 translate-y-0 sm:max-w-none"
      >
        <DialogHeader
          className="flex-row items-center justify-between gap-2 space-y-0 border-b border-border px-4 py-3 text-left shrink-0"
          style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
        >
          <DialogTitle>Filter by plan</DialogTitle>
          <DialogDescription className="sr-only">
            Search and select plans to filter records by which plan they belong
            to.
          </DialogDescription>
          <DialogClose
            aria-label="Close"
            className="-mr-1 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </DialogClose>
        </DialogHeader>
        <PlanPicker
          plans={plans}
          selectedIds={selectedIds}
          onToggle={onToggle}
          className="min-h-0 flex-1"
        />
        <div
          className="shrink-0 border-t border-border bg-background px-4 pt-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <DialogClose asChild>
            <Button className="w-full">Done</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
